import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * ClickableStatCard — Phase 2F operator command-center
 *
 * Renders a labeled count card that navigates to a drill-down route on
 * click. Replaces the static <StatCard> previously used on
 * TenantDetailPage.
 *
 *   <ClickableStatCard
 *     label="Stores"
 *     value={12}
 *     to={`/platform/companies/${cid}/stores`}
 *     testid="card-stores"
 *   />
 *
 * Behaviour:
 *   - Keyboard accessible (focusable button, Enter/Space activate).
 *   - Hover lifts the card; press has a subtle inset.
 *   - Caret in the top-right hints affordance.
 *   - When `to` is falsy, falls back to a non-interactive card so the
 *     summary still renders during refactors-in-progress.
 */
export default function ClickableStatCard({
  label,
  value,
  to,
  testid,
  hint,
}) {
  const nav = useNavigate();
  const interactive = !!to;
  const onClick = () => {
    if (to) nav(to);
  };
  const Tag = interactive ? "button" : "div";
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={onClick}
      className={[
        "bg-paper-2 rounded-card shadow-rest p-4 text-left w-full",
        "transition-all duration-150 ease-out",
        interactive
          ? "cursor-pointer hover:shadow-raised hover:-translate-y-px active:translate-y-0 active:shadow-press focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          : "cursor-default",
      ].join(" ")}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="data-sku">{label}</div>
        {interactive && (
          <ArrowUpRight
            size={14}
            className="text-muted-foreground shrink-0 -mt-0.5"
            aria-hidden
          />
        )}
      </div>
      <div className="font-display font-black text-2xl tabular-nums mt-1">
        {value ?? "—"}
      </div>
      {hint && (
        <div className="font-mono text-[11px] text-muted-foreground mt-1 leading-snug">
          {hint}
        </div>
      )}
    </Tag>
  );
}
